import * as React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFieldArray, useForm, useFormContext, useWatch, Controller } from "react-hook-form";
import { Button } from "../../../../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../../components/ui/dialog";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Switch } from "../../../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { stateOptions } from "data";
import { FieldError } from "../../../runQuote/components/FieldError";
import { Step3Schema } from "../../utils/schema";

const PRIMARY_USE_OPTIONS = [
  { value: "Personal", label: "Personal" },
  { value: "Business", label: "Business" },
  { value: "Commute", label: "Commute" },
  { value: "Farm", label: "Farm" },
  { value: "Other", label: "Other" },
];

const APD_DEDUCTIBLE_OPTIONS = [
  { value: "$1,000", label: "$1,000" },
  { value: "$1,500", label: "$1,500" },
  { value: "$2,000", label: "$2,000" },
  { value: "$2,500", label: "$2,500" },
];

const emptyVehicle = {
  vin: "",
  year: "",
  make: "",
  model: "",
  garagingZip: "",
  ownershipYears: "",
  ownershipMonths: "",
  primaryUse: "",
  primaryUseOther: "",
  rideShare: false,
  delivery: false,
  annualMiles: "",
  lienholder: false,
  lienholderName: "",
  lienholderAddress: "",
  lienholderAddress2: "",
  lienholderCity: "",
  lienholderState: "",
  lienholderZip: "",
  apdCoverage: false,
  apdDeductible: "",
};

const vehicleSchema = Step3Schema.shape.vehicles.element;

function VehicleDialog({ open, onOpenChange, onSave, defaultValues }) {
  const methods = useForm({
    defaultValues,
    resolver: zodResolver(vehicleSchema),
    mode: "onChange",
  });
  const { register, control, handleSubmit, reset, watch, trigger, formState, setValue } = methods;
  const primaryUse = watch("primaryUse");
  const lienholder = watch("lienholder");
  const apdCoverage = watch("apdCoverage");

  React.useEffect(() => {
    if (!open) return;
    reset(defaultValues);
    trigger();
  }, [defaultValues, reset, open, trigger]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Vehicle Details</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={handleSubmit((values) => {
            onSave(values);
          })}
          className="space-y-6"
        >
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="vin">VIN</Label>
              <Input id="vin" placeholder="Enter..." {...register("vin")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="year">Year</Label>
              <Input id="year" type="number" placeholder="YYYY" {...register("year")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="make">Make</Label>
              <Input id="make" placeholder="Enter..." {...register("make")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="model">Model</Label>
              <Input id="model" placeholder="Enter..." {...register("model")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="garagingZip">Garaging Zip</Label>
              <Input id="garagingZip" placeholder="Enter..." {...register("garagingZip")} />
            </div>
            <div className="space-y-2">
              <Label>How long has the customer owned/leased this vehicle?</Label>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  type="number"
                  placeholder="Years"
                  {...register("ownershipYears")}
                />
                <Input
                  type="number"
                  placeholder="Months"
                  {...register("ownershipMonths")}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Primary Vehicle Use</Label>
              <Controller
                control={control}
                name="primaryUse"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Use</SelectLabel>
                        {PRIMARY_USE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            {primaryUse === "Other" && (
              <div className="space-y-2">
                <Label htmlFor="primaryUseOther">Other Primary Use</Label>
                <Input id="primaryUseOther" placeholder="Enter..." {...register("primaryUseOther")} />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="annualMiles">Annual Miles</Label>
              <Input id="annualMiles" type="number" placeholder="Enter..." {...register("annualMiles")} />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex items-center gap-3">
              <Controller
                control={control}
                name="rideShare"
                render={({ field }) => (
                  <Switch id="rideShare" checked={!!field.value} onCheckedChange={field.onChange} />
                )}
              />
              <Label htmlFor="rideShare">Is the vehicle used for ride share? (Uber, Doordash, etc.)</Label>
            </div>
            <div className="flex items-center gap-3">
              <Controller
                control={control}
                name="delivery"
                render={({ field }) => (
                  <Switch id="delivery" checked={!!field.value} onCheckedChange={field.onChange} />
                )}
              />
              <Label htmlFor="delivery">Is the vehicle used for delivery? (Uber Eats, Amazon etc.)</Label>
            </div>
            <div className="flex items-center gap-3">
              <Controller
                control={control}
                name="lienholder"
                render={({ field }) => (
                  <Switch id="lienholder" checked={!!field.value} onCheckedChange={field.onChange} />
                )}
              />
              <Label htmlFor="lienholder">Lienholder</Label>
            </div>
            <div className="flex items-center gap-3">
              <Controller
                control={control}
                name="apdCoverage"
                render={({ field }) => (
                  <Switch
                    id="apdCoverage"
                    checked={!!field.value}
                    onCheckedChange={(checked) => {
                      field.onChange(checked);
                      if (!checked) {
                        setValue("apdDeductible", "");
                      }
                    }}
                  />
                )}
              />
              <Label htmlFor="apdCoverage">Include APD Coverage</Label>
            </div>
          </div>

          {apdCoverage && (
            <div className="space-y-2">
              <Label>APD Deductible:</Label>
              <Controller
                control={control}
                name="apdDeductible"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Deductible</SelectLabel>
                        {APD_DEDUCTIBLE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          )}

          {lienholder && (
            <div className="space-y-4">
              <h4 className="text-base font-semibold text-slate-900">Lienholder Details</h4>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="lienholderName">Lienholder Name</Label>
                  <Input id="lienholderName" placeholder="Enter..." {...register("lienholderName")} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lienholderAddress">Lienholder Address</Label>
                  <Input id="lienholderAddress" placeholder="Enter..." {...register("lienholderAddress")} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lienholderAddress2">STE/Floor (optional)</Label>
                  <Input id="lienholderAddress2" placeholder="Enter..." {...register("lienholderAddress2")} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lienholderCity">City</Label>
                  <Input id="lienholderCity" placeholder="Enter..." {...register("lienholderCity")} />
                </div>
                <div className="space-y-2">
                  <Label>State</Label>
                  <Controller
                    control={control}
                    name="lienholderState"
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60 overflow-y-auto">
                          <SelectGroup>
                            <SelectLabel>States</SelectLabel>
                            {stateOptions.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lienholderZip">Zip</Label>
                  <Input id="lienholderZip" placeholder="Enter..." {...register("lienholderZip")} />
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button
              type="submit"
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={!formState.isValid}
            >
              Save Vehicle
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function Vehicles() {
  const { control } = useFormContext();
  const { fields, append, update, remove } = useFieldArray({
    control,
    name: "step3.vehicles",
  });
  const vehicles = useWatch({ control, name: "step3.vehicles" }) || [];

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingIndex, setEditingIndex] = React.useState(null);

  const openCreate = () => {
    setEditingIndex(null);
    setDialogOpen(true);
  };

  const openEdit = (index) => {
    setEditingIndex(index);
    setDialogOpen(true);
  };

  const handleSave = (values) => {
    if (editingIndex === null) {
      append(values);
    } else {
      update(editingIndex, values);
    }
    setDialogOpen(false);
  };

  const defaultValues =
    editingIndex === null ? emptyVehicle : vehicles[editingIndex] || emptyVehicle;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">Vehicles</h2>
        <Button
          type="button"
          className="bg-red-600 hover:bg-red-700 text-white"
          onClick={openCreate}
          disabled={vehicles.length >= 6}
        >
          Add Vehicle
        </Button>
      </div>

      {vehicles.length >= 6 && (
        <p className="text-sm text-gray-500">Maximum of 6 vehicles reached.</p>
      )}

      <div className="space-y-3">
        {fields.length === 0 && (
          <p className="text-sm text-gray-500">No vehicles added yet.</p>
        )}
        {fields.map((field, index) => {
          const vehicle = vehicles[index] || field;
          const title = `${vehicle.year || "Year"} ${vehicle.make || "Make"} ${vehicle.model || "Model"}`;
          return (
            <div
              key={field.id}
              className="flex items-center justify-between rounded-lg border border-slate-200 p-4"
            >
              <div>
                <div className="text-sm font-semibold text-slate-900">
                  Vehicle {index + 1}: {title}
                </div>
                <div className="text-xs text-slate-500">VIN: {vehicle.vin || "N/A"}</div>
              </div>
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" onClick={() => openEdit(index)}>
                  Edit
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="border-red-200 text-red-600 hover:bg-red-50"
                  onClick={() => remove(index)}
                >
                  Remove
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <FieldError step="step3" name="step3.vehicles" />

      <VehicleDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSave={handleSave}
        defaultValues={defaultValues}
      />
    </div>
  );
}
